import { useState, useEffect } from 'react'
import { CardListView } from './CardListView'
import { StudyMode } from './StudyMode'
import { SequentialStudyMode } from './SequentialStudyMode'
import { Settings } from './Settings'
import { Stats } from './Stats'
import { HelpModal } from './HelpModal'
import { SyncStatusIndicator } from './SyncStatus'
import { HomeView } from './HomeView'
import { StudyAreaView } from './StudyAreaView'
import { ExerciseDetailView } from './ExerciseDetailView'
import { UtilityNav } from './UtilityNav'
import Lessons from './Lessons'
import { useHashRouter } from '../lib/router'
import {
  getShowSyncStatus,
  setShowSyncStatus as saveShowSyncStatus,
  getHideHelpHint,
  setHideHelpHint,
  getStudySelectedTags,
  setStudySelectedTags as saveStudySelectedTags,
  getStudySelectedExercises,
  setStudySelectedExercises as saveStudySelectedExercises,
} from '../lib/storage'

export function Dashboard() {
  const { route, navigate } = useHashRouter()

  const [showHelp, setShowHelp] = useState(false)
  const [studyMountKey, setStudyMountKey] = useState(0)
  const [showSyncStatus, setShowSyncStatus] = useState(() => getShowSyncStatus())
  const [showHelpHint, setShowHelpHint] = useState(() => !getHideHelpHint())

  // StudyMode persistent state (kept in localStorage)
  const [studySessionStarted, setStudySessionStarted] = useState(false)
  const [studySelectedTags, setStudySelectedTags] = useState<string[]>(() => getStudySelectedTags())
  const [studySelectedExercises, setStudySelectedExercises] = useState<string[]>(() =>
    getStudySelectedExercises()
  )
  const [shouldResetStudy, setShouldResetStudy] = useState(false)
  const [studyAutoStart, setStudyAutoStart] = useState(false)

  // Sequential mode state
  const [sequentialTags, setSequentialTags] = useState<string[]>([])
  const [sequentialExercise, setSequentialExercise] = useState<string>('')

  // Persist study preferences to localStorage
  useEffect(() => { saveStudySelectedTags(studySelectedTags) }, [studySelectedTags])
  useEffect(() => { saveStudySelectedExercises(studySelectedExercises) }, [studySelectedExercises])

  // Keyboard shortcut for help
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === '?') setShowHelp(true)
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  // Listen for changes to sync status setting
  useEffect(() => {
    const handleStorageChange = () => setShowSyncStatus(getShowSyncStatus())
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const handleCloseHelpHint = () => {
    setShowHelpHint(false)
    setHideHelpHint(true)
  }

  const handleCloseSyncStatus = () => {
    setShowSyncStatus(false)
    saveShowSyncStatus(false)
  }

  // Navigation handlers
  const handleSelectArea = (areaId: string) => navigate(`/area/${areaId}`)
  const handleBackToHome = () => navigate('/')
  const handleSelectExercise = (exerciseId: string) => navigate(`/exercise/${exerciseId}`)

  const handleStartExerciseStudy = (exerciseId: string) => {
    setStudySelectedTags([])
    setStudySelectedExercises([exerciseId])
    setStudySessionStarted(false)
    setStudyAutoStart(true)
    setStudyMountKey(prev => prev + 1)
    navigate('/study')
  }

  const handleStartExerciseSequential = (exerciseId: string) => {
    setSequentialTags([])
    setSequentialExercise(exerciseId)
    navigate('/sequential')
  }

  const handleViewLesson = (_lessonId: string) => {
    navigate('/lessons')
  }

  const handleUtilityNavigate = (view: 'stats' | 'settings' | 'cards') => {
    navigate(`/${view}`)
  }

  const handleExitStudy = () => {
    const areaId = route.params.fromArea
    if (areaId) {
      navigate(`/area/${areaId}`)
    } else {
      navigate('/')
    }
  }

  // Determine the current view and params from the route
  const currentView = route.view
  const selectedAreaId = route.params.areaId || ''
  const selectedExerciseId = route.params.exerciseId || ''
  const sourceStudyId = route.params.sourceId || ''
  const sourceStudyName = route.params.name || ''

  const showHeader = !['study', 'sequential', 'source-study'].includes(currentView)

  return (
    <div className="min-h-screen bg-gray-50">
      {showHeader && (
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={handleBackToHome}
                  className="flex items-center gap-2 text-gray-900 font-semibold hover:text-gray-700"
                >
                  <span className="text-xl">Learn</span>
                </button>
              </div>
              <div className="flex items-center">
                <UtilityNav onNavigate={handleUtilityNavigate} currentView={currentView} />
              </div>
            </div>
          </div>
        </nav>
      )}

      <main className={`max-w-7xl mx-auto ${showHeader ? 'py-6' : 'py-0'} px-4 sm:px-6 lg:px-8`}>
        {currentView === 'home' ? (
          <HomeView onSelectArea={handleSelectArea} />
        ) : currentView === 'area' && selectedAreaId ? (
          <StudyAreaView
            areaId={selectedAreaId}
            onBack={handleBackToHome}
            onSelectExercise={handleSelectExercise}
          />
        ) : currentView === 'exercise' && selectedExerciseId ? (
          <ExerciseDetailView
            exerciseId={selectedExerciseId}
            onBack={() => window.history.back()}
            onStartStudy={handleStartExerciseStudy}
            onStartSequential={handleStartExerciseSequential}
            onViewLesson={handleViewLesson}
          />
        ) : currentView === 'study' ? (
          <StudyMode
            key={`study-mode-${studyMountKey}`}
            persistentSessionStarted={studySessionStarted}
            setPersistentSessionStarted={setStudySessionStarted}
            persistentSelectedTags={studySelectedTags}
            setPersistentSelectedTags={setStudySelectedTags}
            persistentSelectedExercises={studySelectedExercises}
            setPersistentSelectedExercises={setStudySelectedExercises}
            shouldReset={shouldResetStudy}
            onResetComplete={() => setShouldResetStudy(false)}
            onAllCardsComplete={() => {
              handleExitStudy()
              setShouldResetStudy(true)
            }}
            onStartSequential={(tags, exercise) => {
              setSequentialTags(tags)
              setSequentialExercise(exercise)
              navigate('/sequential')
            }}
            onExit={handleExitStudy}
            autoStart={studyAutoStart}
          />
        ) : currentView === 'sequential' ? (
          <SequentialStudyMode
            selectedTags={sequentialTags}
            selectedExercise={sequentialExercise}
            onExit={handleExitStudy}
          />
        ) : currentView === 'source-study' ? (
          <SequentialStudyMode
            selectedTags={[]}
            selectedExercise=""
            sourceId={sourceStudyId}
            sourceName={sourceStudyName}
            onExit={handleExitStudy}
          />
        ) : currentView === 'lessons' || currentView === 'lesson-view' ? (
          <Lessons
            onStartStudy={() => {
              setStudyMountKey(prev => prev + 1)
              navigate('/study')
            }}
          />
        ) : currentView === 'cards' ? (
          <CardListView />
        ) : currentView === 'settings' ? (
          <Settings />
        ) : currentView === 'stats' ? (
          <Stats />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Unknown view</p>
            <button onClick={handleBackToHome} className="mt-4 text-indigo-600 hover:text-indigo-800">
              Go home
            </button>
          </div>
        )}
      </main>

      {showHelpHint && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 text-sm text-gray-500 bg-white px-3 py-2 rounded-md shadow-sm">
          <span>type ? for help</span>
          <button
            onClick={handleCloseHelpHint}
            className="text-gray-400 hover:text-gray-600 ml-1"
            aria-label="Close help hint"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />

      {showSyncStatus && <SyncStatusIndicator onClose={handleCloseSyncStatus} />}
    </div>
  )
}
